import React from "react";

import {
    ElementNode,
    Node,
    appendChild,
    createElement,
    fragment,
} from "./tree";
import { Block, BlockMap } from "./types";

// Internal type for resolved element - can be string or component
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ResolvedElement = string | React.ComponentType<any>;

/**
 * Resolves a block map entry to its options
 */
function resolveBlockOptions(
    blockMap: BlockMap,
    blockType: string,
): {
    element: ResolvedElement;
    props: Record<string, unknown>;
    wrapper?: ResolvedElement;
    wrapperProps?: Record<string, unknown>;
} {
    const fallback = { element: "div" as ResolvedElement, props: {} };
    const config = blockMap[blockType];

    if (!config) {
        return fallback;
    }

    if (typeof config === "string" || typeof config === "function") {
        return { element: config, props: {} };
    }

    return {
        element: config.element,
        props: config.props ?? {},
        wrapper: config.wrapper,
        wrapperProps: config.wrapperProps,
    };
}

/**
 * A Wrapper represents a container element (like <ul> or <ol>) at a specific depth.
 */
class Wrapper {
    public lastChild: ElementNode | null = null;
    public readonly elt: ElementNode;
    public readonly type: ResolvedElement | undefined;
    public readonly props: Record<string, unknown> | undefined;

    constructor(
        public readonly depth: number,
        options?: {
            wrapper: ResolvedElement;
            wrapperProps?: Record<string, unknown>;
            blockType: string;
        },
    ) {
        if (options) {
            this.type = options.wrapper;
            this.props = options.wrapperProps;

            const wrapperProps = {
                ...(options.wrapperProps ?? {}),
                block: { type: options.blockType, depth },
            };

            this.elt = createElement(options.wrapper, wrapperProps);
        } else {
            this.type = undefined;
            this.props = undefined;
            this.elt = fragment() as unknown as ElementNode; // Placeholder for empty wrapper
        }
    }

    isDifferent(
        depth: number,
        type: ResolvedElement | undefined,
        props: Record<string, unknown> | undefined,
    ): boolean {
        return (
            depth > this.depth ||
            type !== this.type ||
            JSON.stringify(props) !== JSON.stringify(this.props)
        );
    }
}

/**
 * Stack of wrappers. Bottom = closest to body, top = most nested.
 */
class WrapperStack {
    private stack: Wrapper[] = [];

    length(): number {
        return this.stack.length;
    }

    append(wrapper: Wrapper): void {
        this.stack.push(wrapper);
    }

    get(index: number): Wrapper {
        return this.stack[index];
    }

    slice(length: number): void {
        this.stack = this.stack.slice(0, length);
    }

    head(): Wrapper {
        if (this.stack.length > 0) {
            return this.stack[this.stack.length - 1];
        }
        return new Wrapper(-1);
    }

    tail(): Wrapper {
        return this.stack[0];
    }

    reset(): void {
        this.stack = [];
    }
}

/**
 * WrapperState manages block-level nesting (lists, indentation).
 *
 * It builds the DOM tree structure for blocks, handling:
 * - Simple blocks (paragraphs, headings) - no wrapping
 * - Wrapped blocks (list items) - nested in ul/ol based on depth
 */
export class WrapperState {
    private stack = new WrapperStack();
    private lastReturnedWrapper: ElementNode | null = null;

    constructor(
        private blockMap: BlockMap,
        public readonly blocks: Block<string, string>[],
    ) {}

    /**
     * Create an element for a block, with appropriate wrapper nesting.
     * Returns the element that should be appended to the document,
     * or null if the element was added to an existing wrapper.
     */
    elementFor(block: Block<string, string>, blockContent: Node): Node | null {
        const blockType =
            ("type" in block ? block.type : undefined) ?? "unstyled";
        const depth = ("depth" in block ? block.depth : undefined) ?? 0;

        const options = resolveBlockOptions(this.blockMap, blockType);
        const props = {
            ...options.props,
            block,
            blocks: this.blocks,
        };

        // Create the block element with content
        const elt = createElement(options.element, props, blockContent);

        // Wrap if needed
        return this.parentFor(options, depth, elt, blockType);
    }

    /**
     * Determine the parent for an element - either a wrapper or the element itself.
     * Returns null if this element was added to an existing wrapper (no need to append to doc).
     */
    private parentFor(
        options: ReturnType<typeof resolveBlockOptions>,
        depth: number,
        elt: ElementNode,
        blockType: string,
    ): Node | null {
        if (options.wrapper) {
            const parent = this.getWrapperElt(options, depth, blockType);
            appendChild(parent, elt);
            this.stack.head().lastChild = elt;

            // Get the root wrapper (depth 0)
            const rootWrapper = this.stack.get(0).elt;

            // Only return the wrapper if it's new (not the same as last time)
            if (rootWrapper === this.lastReturnedWrapper) {
                return null; // Already in document, don't add again
            }
            this.lastReturnedWrapper = rootWrapper;
            return rootWrapper;
        } else {
            // Reset the stack if there is no wrapper
            if (this.stack.length() > 0) {
                this.stack.reset();
                this.lastReturnedWrapper = null;
            }
            return elt;
        }
    }

    /**
     * Get or create a wrapper element at the specified depth.
     */
    private getWrapperElt(
        options: ReturnType<typeof resolveBlockOptions>,
        depth: number,
        blockType: string,
    ): ElementNode {
        const head = this.stack.head();

        if (head.isDifferent(depth, options.wrapper, options.wrapperProps)) {
            this.updateStack(options, depth, blockType);
        }

        // If depth is lower than the maximum, cut the stack
        if (depth < head.depth) {
            this.stack.slice(depth + 1);
        }

        return this.stack.get(depth).elt;
    }

    /**
     * Update the wrapper stack for the new depth level.
     */
    private updateStack(
        options: ReturnType<typeof resolveBlockOptions>,
        depth: number,
        blockType: string,
    ): void {
        if (depth >= this.stack.length()) {
            // Need more wrappers - add levels
            for (let level = this.stack.length(); level <= depth; level++) {
                const newWrapper = new Wrapper(level, {
                    wrapper: options.wrapper!,
                    wrapperProps: options.wrapperProps,
                    blockType,
                });

                const head = this.stack.head();

                // Determine where to append the new wrapper
                let wrapperParent: ElementNode;

                if (head.lastChild === null) {
                    // No content in current wrapper - need intermediary node
                    const props = {
                        ...options.props,
                        block: { type: blockType, depth, data: {} },
                        blocks: this.blocks,
                    };
                    wrapperParent = createElement(options.element, props);
                    appendChild(head.elt, wrapperParent);
                } else {
                    // Append at the end of the last child
                    wrapperParent = head.lastChild;
                }

                appendChild(wrapperParent, newWrapper.elt);
                this.stack.append(newWrapper);
            }
        } else {
            // Cut and add new wrapper
            this.stack.slice(depth);
            this.stack.append(
                new Wrapper(depth, {
                    wrapper: options.wrapper!,
                    wrapperProps: options.wrapperProps,
                    blockType,
                }),
            );
        }
    }

    /**
     * Get the root element if all blocks were nested (depth > 0)
     */
    getRootIfNested(): Node | null {
        if (this.stack.length() > 0) {
            return this.stack.tail().elt;
        }
        return null;
    }
}
